import { createContext, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import type { QueryClient } from '@tanstack/react-query';
import * as authApi from '../../api/auth.ts';
import type { User } from '../../api/auth.ts';
import { setUnauthorizedHandler } from '../../api/client.ts';

export type AuthStatus = 'checking' | 'authenticated' | 'anonymous';

export interface AuthContextValue {
  status: AuthStatus;
  user: User | null;
  /** True si al usuario lo ha echado el servidor (401), no si se fue él. */
  sessionExpired: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (payload: authApi.RegisterPayload) => Promise<void>;
  logout: () => Promise<void>;
  clearSessionExpired: () => void;
}

export const AuthContext = createContext<AuthContextValue | null>(null);

/**
 * Estado de sesión de la aplicación.
 *
 * El cliente NO puede leer la cookie de sesión: es httpOnly (ADR 0003). Así que la
 * única forma de saber si hay sesión es preguntárselo al servidor con `GET /auth/me`.
 * Eso es una ventaja, no un inconveniente: lo que aquí se guarda es una copia de lo que
 * el servidor ya ha dicho, nunca la autoridad. Cualquier endpoint protegido vuelve a
 * comprobarlo por su cuenta.
 */
export function AuthProvider({
  children,
  queryClient,
}: {
  children: ReactNode;
  queryClient: QueryClient;
}) {
  const [status, setStatus] = useState<AuthStatus>('checking');
  const [user, setUser] = useState<User | null>(null);
  const [sessionExpired, setSessionExpired] = useState(false);
  // Evita avisar de sesión caducada cuando ya sabíamos que no había ninguna.
  const wasAuthenticated = useRef(false);

  /**
   * Al perder la sesión hay que TIRAR la caché de consultas, no solo dejar de usarla.
   *
   * Si no, las ligas, los pronósticos y la clasificación del usuario anterior siguen en
   * memoria y se pintan durante un instante en cuanto otra persona entra en el mismo
   * dispositivo, antes de que llegue la primera respuesta del servidor. En un teléfono
   * compartido eso es una fuga de datos, aunque dure medio segundo.
   */
  const olvidarSesion = useCallback(() => {
    setUser(null);
    setStatus('anonymous');
    wasAuthenticated.current = false;
    queryClient.clear();
  }, [queryClient]);

  useEffect(() => {
    const controller = new AbortController();
    authApi
      .fetchCurrentUser(controller.signal)
      .then((current) => {
        setUser(current);
        wasAuthenticated.current = current !== null;
        setStatus(current ? 'authenticated' : 'anonymous');
      })
      .catch((error: unknown) => {
        if (error instanceof DOMException && error.name === 'AbortError') return;
        // Si el servidor no responde no se puede afirmar que haya sesión.
        setUser(null);
        setStatus('anonymous');
      });
    return () => controller.abort();
  }, []);

  /**
   * Reacción central al 401 (requisito 3). Se registra una vez y sirve para toda la
   * aplicación: ninguna pantalla tiene que acordarse de comprobar si la sesión sigue
   * viva. La redirección la hace el enrutado al ver `status === 'anonymous'`.
   */
  useEffect(() => {
    setUnauthorizedHandler(() => {
      const teniaSesion = wasAuthenticated.current;
      olvidarSesion();
      if (teniaSesion) setSessionExpired(true);
    });
    return () => setUnauthorizedHandler(() => {});
  }, [olvidarSesion]);

  const adopt = useCallback(
    (current: User) => {
      // Vaciar también al ENTRAR, no solo al salir: si en este dispositivo entra otra
      // persona sin haber cerrado sesión antes (sesión caducada, por ejemplo), la caché
      // podría conservar ligas del anterior.
      queryClient.clear();
      setUser(current);
      setStatus('authenticated');
      setSessionExpired(false);
      wasAuthenticated.current = true;
    },
    [queryClient],
  );

  const login = useCallback(
    async (email: string, password: string) => {
      adopt(await authApi.login(email, password));
    },
    [adopt],
  );

  const register = useCallback(
    async (payload: authApi.RegisterPayload) => {
      // El backend abre sesión al registrar, así que no hay que iniciarla otra vez.
      adopt(await authApi.register(payload));
    },
    [adopt],
  );

  const logout = useCallback(async () => {
    try {
      await authApi.logout();
    } finally {
      // Pase lo que pase con la petición, en este navegador la sesión se acaba aquí.
      // El servidor la revoca de verdad; esto solo es el estado de la interfaz.
      olvidarSesion();
      setSessionExpired(false);
    }
  }, [olvidarSesion]);

  const clearSessionExpired = useCallback(() => setSessionExpired(false), []);

  const value = useMemo<AuthContextValue>(
    () => ({ status, user, sessionExpired, login, register, logout, clearSessionExpired }),
    [status, user, sessionExpired, login, register, logout, clearSessionExpired],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
